import types from "../http_common";
import { apiRequest } from "../authConfig";

async function bearerToken(instance) {
  const account = instance.getActiveAccount();
  if (!account) {
    throw Error(
      "No active account! Verify a user has been signed in and setActiveAccount has been called."
    );
  }
  const response = await instance.acquireTokenSilent({
    ...apiRequest,
    account: account,
  });
  return response.accessToken;
}

/**
 * Fetch all measures (flat list from the backend).
 * The UI groups them into Properties → Jobs → Measures.
 */
const getMeasures = async (instance, filters = {}) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;

  const params = new URLSearchParams();
  if (filters.postcode) params.append("postcode", filters.postcode);
  if (filters.address) params.append("address", filters.address);
  if (filters.jobId) params.append("jobId", filters.jobId);
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.measureStatus) params.append("measureStatus", filters.measureStatus);
  if (filters.pasMeasureName) params.append("pasMeasureName", filters.pasMeasureName);
  if (filters.lodgementStatus !== undefined && filters.lodgementStatus !== null && String(filters.lodgementStatus) !== "") {
    params.append("lodgementStatus", String(filters.lodgementStatus));
  }
  if (filters.fundingCategory) params.append("fundingCategory", filters.fundingCategory);
  if (filters.fundingName) params.append("fundingName", filters.fundingName);
  if (filters.schemeName) params.append("schemeName", filters.schemeName);
  if (filters.onHold !== undefined && filters.onHold !== null && String(filters.onHold) !== "") {
    params.append("onHold", String(filters.onHold));
  }
  if (filters.cancelledJobs !== undefined && filters.cancelledJobs !== null && String(filters.cancelledJobs) !== "") {
    params.append("cancelledJobs", String(filters.cancelledJobs));
  }
  if (filters.createdFrom) params.append("createdFrom", filters.createdFrom);
  if (filters.createdTo) params.append("createdTo", filters.createdTo);
  if (filters.jobStatus !== undefined && filters.jobStatus !== null && String(filters.jobStatus) !== "") {
    params.append("jobStatus", String(filters.jobStatus));
  }
  if (filters.jobType !== undefined && filters.jobType !== null && String(filters.jobType) !== "") {
    params.append("jobType", String(filters.jobType));
  }
  if (filters.pasVersion) params.append("pasVersion", filters.pasVersion);
  if (filters.uprn) params.append("uprn", filters.uprn);
  if (filters.assessmentRef) params.append("assessmentRef", filters.assessmentRef);
  if (filters.surveyDampMould !== undefined && filters.surveyDampMould !== null && String(filters.surveyDampMould) !== "") {
    params.append("surveyDampMould", String(filters.surveyDampMould));
  }
  if (filters.surveyHhsrs !== undefined && filters.surveyHhsrs !== null && String(filters.surveyHhsrs) !== "") {
    params.append("surveyHhsrs", String(filters.surveyHhsrs));
  }
  if (filters.surveySap !== undefined && filters.surveySap !== null && String(filters.surveySap) !== "") {
    params.append("surveySap", String(filters.surveySap));
  }
  if (filters.surveyWallType) params.append("surveyWallType", filters.surveyWallType);
  if (filters.surveyBuiltForm) params.append("surveyBuiltForm", filters.surveyBuiltForm);
  if (filters.lifecycleStatus) params.append("lifecycleStatus", filters.lifecycleStatus);
  if (filters.jobsWithoutMeasures) params.append("jobsWithoutMeasures", "true");
  if (filters.page) params.append("page", String(filters.page));
  if (filters.pageSize) params.append("pageSize", String(filters.pageSize));

  const qs = params.toString();
  return await api.get(`/measures${qs ? "?" + qs : ""}`);
};

/**
 * Free-text search across postcode/address/measure/job fields.
 */
const searchMeasures = async (instance, q, options = {}) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;

  const params = new URLSearchParams();
  params.append("q", q);
  if (options.lodgementStatus !== undefined && options.lodgementStatus !== null && String(options.lodgementStatus) !== "") {
    params.append("lodgementStatus", String(options.lodgementStatus));
  }
  if (options.fundingCategory) params.append("fundingCategory", options.fundingCategory);
  if (options.fundingName) params.append("fundingName", options.fundingName);
  if (options.schemeName) params.append("schemeName", options.schemeName);
  if (options.onHold !== undefined && options.onHold !== null && String(options.onHold) !== "") {
    params.append("onHold", String(options.onHold));
  }
  if (options.cancelledJobs !== undefined && options.cancelledJobs !== null && String(options.cancelledJobs) !== "") {
    params.append("cancelledJobs", String(options.cancelledJobs));
  }
  if (options.createdFrom) params.append("createdFrom", options.createdFrom);
  if (options.createdTo) params.append("createdTo", options.createdTo);
  if (options.jobStatus !== undefined && options.jobStatus !== null && String(options.jobStatus) !== "") {
    params.append("jobStatus", String(options.jobStatus));
  }
  if (options.jobType !== undefined && options.jobType !== null && String(options.jobType) !== "") {
    params.append("jobType", String(options.jobType));
  }
  if (options.pasVersion) params.append("pasVersion", options.pasVersion);
  if (options.uprn) params.append("uprn", options.uprn);
  if (options.assessmentRef) params.append("assessmentRef", options.assessmentRef);
  if (options.surveyDampMould !== undefined && options.surveyDampMould !== null && String(options.surveyDampMould) !== "") {
    params.append("surveyDampMould", String(options.surveyDampMould));
  }
  if (options.surveyHhsrs !== undefined && options.surveyHhsrs !== null && String(options.surveyHhsrs) !== "") {
    params.append("surveyHhsrs", String(options.surveyHhsrs));
  }
  if (options.surveySap !== undefined && options.surveySap !== null && String(options.surveySap) !== "") {
    params.append("surveySap", String(options.surveySap));
  }
  if (options.surveyWallType) params.append("surveyWallType", options.surveyWallType);
  if (options.surveyBuiltForm) params.append("surveyBuiltForm", options.surveyBuiltForm);
  if (options.lifecycleStatus) params.append("lifecycleStatus", options.lifecycleStatus);
  if (options.pasMeasureName) params.append("pasMeasureName", options.pasMeasureName);
  if (options.page) params.append("page", String(options.page));
  if (options.pageSize) params.append("pageSize", String(options.pageSize));

  return await api.get(`/measures/search?${params.toString()}`);
};

/**
 * Fetch a single property's details by JobUId.
 */
const getPropertyByJobUId = async (instance, jobUId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/property/${jobUId}`);
};

/**
 * Fetch job-level detail.
 */
const getJobDetail = async (instance, jobId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/job/${jobId}`);
};

/**
 * Fetch stages for a given measure.
 */
const getMeasureStages = async (instance, measureId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/${measureId}/stages`);
};

/**
 * Fetch evidence items for a given measure.
 */
const getMeasureEvidence = async (instance, measureId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/${measureId}/evidence`);
};

/**
 * Fetch recommended measures (MeasureModel) for a given job.
 */
const getJobRecommendations = async (instance, jobId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/job/${jobId}/recommendations`);
};

/**
 * Fetch job-level operational + retrofit insights.
 */
const getJobInsights = async (instance, jobId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/job/${jobId}/insights`);
};

/**
 * Fetch detailed records behind job insights.
 */
const getJobInsightsDetails = async (instance, jobId) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get(`/measures/job/${jobId}/insights/details`);
};

/**
 * Fetch aggregate totals for all actioned measures (properties, jobs, measure count).
 */
const getMeasuresTotals = async (instance) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get("/measures/totals");
};

const getJobStatusCounts = async (instance) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get("/measures/job-status-counts");
};

/**
 * Fetch dashboard-level data health coverage metrics.
 */
const getInsightsOverview = async (instance) => {
  const api = types.json();
  const token = await bearerToken(instance);
  api.defaults.headers.common.Authorization = "Bearer " + token;
  return await api.get("/measures/insights/overview");
};

/**
 * Download an XLSX export of all measures matching the given filters.
 * Triggers a browser file-save dialog.
 */
const downloadExcelExport = async (instance, filters = {}) => {
  const token = await bearerToken(instance);

  const params = new URLSearchParams();
  if (filters.postcode)      params.append("postcode",      filters.postcode);
  if (filters.address)       params.append("address",       filters.address);
  if (filters.jobId)         params.append("jobId",         filters.jobId);
  if (filters.companyId)     params.append("companyId",     filters.companyId);
  if (filters.measureStatus) params.append("measureStatus", filters.measureStatus);
  if (filters.pasMeasureName)params.append("pasMeasureName",filters.pasMeasureName);
    if (filters.lodgementStatus !== undefined && filters.lodgementStatus !== null && String(filters.lodgementStatus) !== "") {
      params.append("lodgementStatus", String(filters.lodgementStatus));
    }
    if (filters.fundingCategory) params.append("fundingCategory", filters.fundingCategory);
    if (filters.fundingName) params.append("fundingName", filters.fundingName);
    if (filters.schemeName) params.append("schemeName", filters.schemeName);
    if (filters.lifecycleStatus) params.append("lifecycleStatus", filters.lifecycleStatus);
    if (filters.onHold !== undefined && filters.onHold !== null && String(filters.onHold) !== "") {
      params.append("onHold", String(filters.onHold));
    }
    if (filters.cancelledJobs !== undefined && filters.cancelledJobs !== null && String(filters.cancelledJobs) !== "") {
      params.append("cancelledJobs", String(filters.cancelledJobs));
    }
    if (filters.createdFrom) params.append("createdFrom", filters.createdFrom);
    if (filters.createdTo) params.append("createdTo", filters.createdTo);
    if (filters.jobStatus !== undefined && filters.jobStatus !== null && String(filters.jobStatus) !== "") {
      params.append("jobStatus", String(filters.jobStatus));
    }
    if (filters.jobType !== undefined && filters.jobType !== null && String(filters.jobType) !== "") {
      params.append("jobType", String(filters.jobType));
    }
    if (filters.pasVersion) params.append("pasVersion", filters.pasVersion);
    if (filters.uprn) params.append("uprn", filters.uprn);
    if (filters.assessmentRef) params.append("assessmentRef", filters.assessmentRef);
    if (filters.surveyDampMould !== undefined && filters.surveyDampMould !== null && String(filters.surveyDampMould) !== "") {
      params.append("surveyDampMould", String(filters.surveyDampMould));
    }
    if (filters.surveyHhsrs !== undefined && filters.surveyHhsrs !== null && String(filters.surveyHhsrs) !== "") {
      params.append("surveyHhsrs", String(filters.surveyHhsrs));
    }
    if (filters.surveySap !== undefined && filters.surveySap !== null && String(filters.surveySap) !== "") {
      params.append("surveySap", String(filters.surveySap));
    }
    if (filters.surveyWallType) params.append("surveyWallType", filters.surveyWallType);
    if (filters.surveyBuiltForm) params.append("surveyBuiltForm", filters.surveyBuiltForm);

  const baseUrl = (types.json().defaults.baseURL || "").replace(/\/$/, "");
  const qs = params.toString();
  const url = `${baseUrl}/measures/export${qs ? "?" + qs : ""}`;

  const response = await fetch(url, {
    headers: { Authorization: "Bearer " + token },
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  const exportedAt = new Date().toISOString().slice(0, 10);
  anchor.href = objectUrl;
  anchor.download = `measures-export-${exportedAt}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const measuresApiService = {
  getMeasures,
  getMeasuresTotals,
  getJobStatusCounts,
  getInsightsOverview,
  searchMeasures,
  getPropertyByJobUId,
  getJobDetail,
  getMeasureStages,
  getMeasureEvidence,
  getJobRecommendations,
  getJobInsights,
  getJobInsightsDetails,
  downloadExcelExport,
};

export default measuresApiService;
